import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  IconButton,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  LocalShipping as TransportIcon,
  SupportAgent as AgentIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { fetchPlanDetail } from "../API";
import processing from "../gif/processing.gif";
import CtTxt from "./CustomTool/CtTxt";
import CtTxtNum from "./CustomTool/CtTxtNum";
import CtBtn from "./CustomTool/CtBtn";
import HoCtToastContainer from "../HOC/HoCtToastContainer";

const PricingPlans = ({ notifyProcessing, closeUpdateProcessing, updateProcessing, toastMsg, toastErrorMsg }) => {
  const theme = useTheme();

  const [openModal, React_useState] = React.useState(false);
  const [selectedPlan, setSelectedPlan] = React.useState("");
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    phone: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenModal = (planName) => {
    setSelectedPlan(planName);
    React_useState(true);
  };

  const handleCloseModal = () => {
    React_useState(false);
    setSelectedPlan("");
    setFormData({ name: "", email: "", phone: "" });
  };

  const handleSubmitInquiry = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toastErrorMsg("Please enter Full Name.");
      document.getElementById('name')?.focus();
      return;
    }

    if (!formData.email.trim()) {
      toastErrorMsg("Please enter Email Address.");
      document.getElementById('email')?.focus();
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toastErrorMsg("Please enter a valid email address.");
      document.getElementById('email')?.focus();
      return;
    }

    if (!formData.phone.trim()) {
      toastErrorMsg("Please enter Phone Number.");
      document.getElementById('phone')?.focus();
      return;
    }

    if (formData.phone.length !== 10) {
      toastErrorMsg("Phone number must be exactly 10 digits.");
      document.getElementById('phone')?.focus();
      return;
    }

    notifyProcessing();

    try {
      const response = await fetchPlanDetail({
        Op: 'SaveInquiry',
        vac_plan_name: selectedPlan,
        vac_customer_name: formData.name,
        vac_email: formData.email,
        vac_phone: formData.phone
      });

      const data = response.data;

      if (data.msgType === 'success') {
        const customerName = formData.name;
        const email = formData.email;
        const phone = formData.phone;

        const whatsappNumber = "918000516051";

        const message = `Hi! I would like to inquire about the *${selectedPlan}*.\n\n*Name:* ${customerName}\n*Email:* ${email}\n*Phone:* ${phone}`;
        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

        updateProcessing("success", data.message || "Inquiry submitted successfully!");
        handleCloseModal();
        setTimeout(() => {
          window.open(whatsappUrl, '_blank');
        }, 2500);
      } else {
        updateProcessing("error", data.message || "Something went wrong.");
      }
    } catch (error) {
      console.error('Error saving inquiry:', error);
      updateProcessing("error", "Failed to connect to the server. Please try again.");
    }
  };

  const commonFeatures = [
    "Sales Entry",
    "Purchase Entry",
    "Receipt Entry",
    "Payment Entry",
    "Sales Report",
    "Purchase Report",
    "Day Book",
    "Ledger Book",
    "Customer Book",
    "Supplier Book",
    "Outstanding Report",
    "Stock Report",
    "Trial Balance",
    "GST Report",
    "E-Way Bill Integration",
    "E-Invoice Integration",
  ];

  const plans = [
    {
      title: "Basic Plan",
      price: "₹3000",
      subtitle: "Without E-Way Bill & E-Invoice",
      highlight: false,
      features: commonFeatures.filter(
        (f) => f !== "E-Way Bill Integration" && f !== "E-Invoice Integration"
      ),
    },
    {
      title: "Premium Plan",
      price: "₹3500",
      subtitle: "Includes E-Way Bill & E-Invoice",
      highlight: true,
      features: commonFeatures,
    },
    {
      title: "Commission Agent Plan",
      price: "Included",
      subtitle: "Includes all Premium features + Agent module",
      highlight: false,
      features: [...commonFeatures, "Commission Agent Module"],
    },
    {
      title: "Transport Plan",
      price: "Included",
      subtitle: "Includes all Premium features + Transport module",
      highlight: false,
      features: [...commonFeatures, "Transport Management Module"],
    },
  ];

  return (
    <Box
      sx={{
        py: 10,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.05
        )}, ${alpha(theme.palette.background.default, 0.8)})`,
      }}
    >
      <Container maxWidth="lg">
        <Box textAlign="center" mb={8}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Simple & Transparent Pricing
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mx: "auto" }}>
            Choose your plan and unlock all accounting & report features — no
            hidden charges, full access.
          </Typography>
        </Box>

        <Grid container spacing={5} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 6 }} key={index}>
              <Card
                elevation={plan.highlight ? 6 : 2}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  height: "93%",
                  border: plan.highlight
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  background: plan.highlight
                    ? `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.08
                    )}, transparent)`
                    : theme.palette.background.paper,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: `0 10px 30px ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                  },
                }}
              >
                <Box textAlign="center" mb={3}>
                  {plan.icon && (
                    <Box display="flex" justifyContent="center" mb={1}>
                      {plan.icon}
                    </Box>
                  )}
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      color: plan.highlight
                        ? theme.palette.primary.main
                        : "inherit",
                    }}
                  >
                    {plan.title}
                  </Typography>
                  <Typography variant="h3" fontWeight={800} my={1}>
                    {plan.price}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {plan.subtitle}
                  </Typography>
                </Box>

                <List dense>
                  {plan.features.map((feature, i) => (
                    <ListItem key={i} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckIcon color="primary" sx={{ fontSize: 22 }} />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>

                <Box display="flex" justifyContent="center">
                  <CtBtn
                    variant={plan.highlight ? "contained" : "outlined"}
                    color="primary"
                    fullWidth
                    onClick={() => handleOpenModal(plan.title)}
                    label={plan.highlight
                      ? "Get Premium Plan"
                      : `Choose ${plan.title.replace(" Plan", "")}`}
                    margin="24px 0 0 0"
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box textAlign="center" mt={8}>
          <Typography variant="subtitle1" color="text.secondary">
            ✅ No hidden fees — full transparency, full control.
          </Typography>
        </Box>
      </Container>

      {openModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: 24, width: '100%', maxWidth: 500 }}>
            <Typography variant="h5" mb={1}>Inquire about {selectedPlan}</Typography>
            <Typography variant="body2" mb={2}>
              Please enter your details below and our team will get in touch with you shortly to set up your {selectedPlan}.
            </Typography>
            <Box component="form" onSubmit={handleSubmitInquiry} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <CtTxt
                id="name"
                label="Full Name"
                value={formData.name}
                handleOnChange={handleInputChange}
                maxLength={100}
                variant="outlined"
                fullWidth
              />
              <CtTxt
                id="email"
                label="Email Address"
                value={formData.email}
                handleOnChange={handleInputChange}
                maxLength={100}
                variant="outlined"
                fullWidth
              />
              <CtTxtNum
                id="phone"
                label="Phone Number"
                value={formData.phone}
                handleOnChange={handleInputChange}
                maxLength={10}
                variant="outlined"
                fullWidth
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <CtBtn
                  label="Cancel"
                  variant="outlined"
                  color="inherit"
                  onClick={handleCloseModal}
                />
                <CtBtn
                  label="Submit Inquiry"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmitInquiry}
                />
              </Box>
            </Box>
          </div>
        </div>
      )}

    </Box>
  );
};

export default HoCtToastContainer(PricingPlans);
